import { BufferTarget, CanvasSource, Mp4OutputFormat, Output, Quality } from 'mediabunny';
import { drawVideoFrame, type PreparedVideo } from './renderer';

export type OutputFormat = 'story' | 'post';
export type VideoDuration = 20 | 30 | 40;

export const VIDEO_DURATIONS: readonly VideoDuration[] = [20, 30, 40];

export interface VideoPreset {
  readonly format: OutputFormat;
  readonly width: number;
  readonly height: number;
  readonly frameRate: number;
  readonly bitrate: number;
  readonly codecCandidates: readonly string[];
}

export interface ResolvedVideoPreset extends VideoPreset {
  readonly codec: string;
}

const PRESETS: Readonly<Record<OutputFormat, readonly VideoPreset[]>> = {
  story: [
    { format: 'story', width: 1080, height: 1920, frameRate: 30, bitrate: 10_000_000, codecCandidates: ['avc1.420028', 'avc1.640028', 'avc1.42002a'] },
    { format: 'story', width: 720, height: 1280, frameRate: 24, bitrate: 5_000_000, codecCandidates: ['avc1.42001f', 'avc1.64001f', 'avc1.420020'] },
  ],
  post: [
    { format: 'post', width: 1080, height: 1080, frameRate: 24, bitrate: 8_000_000, codecCandidates: ['avc1.420020', 'avc1.640020', 'avc1.420028'] },
    { format: 'post', width: 720, height: 720, frameRate: 24, bitrate: 5_000_000, codecCandidates: ['avc1.42001f', 'avc1.64001f'] },
  ],
};

const VIDEO_BRAND_ICON_URL = '/brand/oneultool-icon-128.png';
let brandIconPromise: Promise<HTMLImageElement | null> | null = null;

function loadVideoBrandIcon(): Promise<HTMLImageElement | null> {
  if (brandIconPromise) return brandIconPromise;
  brandIconPromise = new Promise((resolve) => {
    if (typeof Image === 'undefined') {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = VIDEO_BRAND_ICON_URL;
  });
  return brandIconPromise;
}

function mobileDevice(): boolean {
  return matchMedia('(max-width: 767px)').matches || navigator.maxTouchPoints > 1;
}

async function supportedCodec(preset: VideoPreset): Promise<string | null> {
  for (const codec of preset.codecCandidates) {
    try {
      const result = await VideoEncoder.isConfigSupported({
        codec,
        width: preset.width,
        height: preset.height,
        bitrate: preset.bitrate,
        framerate: preset.frameRate,
        hardwareAcceleration: 'no-preference',
      });
      if (result.supported) return codec;
    } catch {
      // Probe the next standards-compliant H.264 profile/level.
    }
  }
  return null;
}

export async function resolveVideoPreset(format: OutputFormat): Promise<ResolvedVideoPreset> {
  if (typeof VideoEncoder === 'undefined') {
    throw new Error('이 브라우저에서는 MP4 영상을 만들 수 없어요. 최신 Chrome에서 다시 시도해 주세요.');
  }
  const candidates = mobileDevice() ? [...PRESETS[format]].reverse() : PRESETS[format];
  for (const preset of candidates) {
    const codec = await supportedCodec(preset);
    if (codec) return { ...preset, codec };
  }
  throw new Error('이 기기에서 사용할 수 있는 영상 인코더를 찾지 못했어요. PC Chrome에서 다시 시도해 주세요.');
}

function isMp4(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 12) return false;
  return String.fromCharCode(...new Uint8Array(buffer, 4, 8)).startsWith('ftyp');
}

export async function createMp4(
  canvas: HTMLCanvasElement,
  video: PreparedVideo,
  preset: ResolvedVideoPreset,
  signal: AbortSignal,
  onProgress: (fraction: number) => void,
): Promise<Blob> {
  const brandIcon = await loadVideoBrandIcon();
  if (signal.aborted) throw new DOMException('영상 만들기가 취소됐습니다.', 'AbortError');
  const renderedVideo = brandIcon ? { ...video, brandIcon } : video;
  const durationSeconds = video.durationSeconds;
  const frameCount = durationSeconds * preset.frameRate;
  const frameDuration = 1 / preset.frameRate;
  const target = new BufferTarget();
  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
    target,
  });
  const source = new CanvasSource(canvas, {
    codec: 'avc',
    fullCodecString: preset.codec,
    quality: new Quality({ bitrate: preset.bitrate }),
    keyFrameInterval: 1,
    hardwareAcceleration: 'no-preference',
  });
  output.addVideoTrack(source, { frameRate: preset.frameRate });
  output.setMetadataTags({ title: `내 역마살 ${durationSeconds}초`, artist: '오늘툴' });
  await output.start();

  try {
    for (let frame = 0; frame < frameCount; frame += 1) {
      if (signal.aborted) throw new DOMException('영상 만들기가 취소됐습니다.', 'AbortError');
      const progress = frameCount === 1 ? 1 : frame / (frameCount - 1);
      drawVideoFrame(canvas, renderedVideo, progress);
      await source.add(frame * frameDuration, frameDuration, {
        keyFrame: frame % preset.frameRate === 0,
      });
      onProgress((frame + 1) / frameCount);
    }
    await output.finalize();
    if (!target.buffer || !isMp4(target.buffer)) {
      throw new Error('완성된 MP4 파일을 확인할 수 없습니다. 다시 시도해 주세요.');
    }
    return new Blob([target.buffer], { type: 'video/mp4' });
  } catch (error) {
    await output.cancel().catch(() => undefined);
    throw error;
  }
}
